import React from 'react';
import { Step } from 'react-joyride';
import {
  PlayCircle, Target, Zap, Copy, Calendar, Users,
  Filter, Columns, Keyboard, CheckCircle, Clock, Settings
} from 'lucide-react';

/**
 * Interactive calendar tutorial steps using React Joyride
 * Covers all content from CalendarTutorialDialog in step-by-step format
 */
export const calendarTutorialSteps: Step[] = [
  // Step 1: Welcome & Overview
  {
    target: '[data-tutorial="calendar-container"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <PlayCircle className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Welcome to the Calendar Tutorial!</h3>
        </div>
        <p className="text-sm">
          The calendar is your central hub for shift scheduling. It provides a visual overview
          of your entire schedule with color-coded validation to ensure everything is properly set up.
        </p>
        <p className="text-sm text-muted-foreground">
          This interactive tour will walk you through all the features and best practices.
        </p>
      </div>
    ),
    disableBeacon: true,
  },

  // Step 2: Calendar Layout
  {
    target: '[data-tutorial="time-grid"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Calendar Layout</h3>
        </div>
        <p className="text-sm">
          The calendar displays days as columns with time slots running vertically.
          Each cell represents a specific time period where you can create shifts.
        </p>
        <p className="text-sm text-muted-foreground">
          Shifts are displayed as cards overlaying the time grid.
        </p>
      </div>
    ),
    disableBeacon: true,
  },

  // Step 3: Schedule Validation - Day Borders
  {
    target: '[data-tutorial="day-column"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Target className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Schedule Validation Status</h3>
        </div>
        <p className="text-sm mb-3">
          Each day has a colored border indicating schedule completeness. This is your quick health check:
        </p>

        <div className="space-y-2">
          <div className="flex items-start gap-2 text-sm">
            <div className="h-3 w-3 rounded-full bg-green-500 mt-0.5 flex-shrink-0" />
            <div>
              <span className="font-medium">Green (Complete):</span>
              <span className="text-muted-foreground"> All shifts assigned, opening hours covered</span>
            </div>
          </div>

          <div className="flex items-start gap-2 text-sm">
            <div className="h-3 w-3 rounded-full bg-orange-500 mt-0.5 flex-shrink-0" />
            <div>
              <span className="font-medium">Orange (Partial):</span>
              <span className="text-muted-foreground"> Some shifts need employees or hours have gaps</span>
            </div>
          </div>

          <div className="flex items-start gap-2 text-sm">
            <div className="h-3 w-3 rounded-full bg-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <span className="font-medium">Red (Incomplete):</span>
              <span className="text-muted-foreground"> No shifts or multiple issues</span>
            </div>
          </div>

          <div className="flex items-start gap-2 text-sm">
            <div className="h-3 w-3 rounded-full bg-gray-500 mt-0.5 flex-shrink-0" />
            <div>
              <span className="font-medium">Gray (Closed):</span>
              <span className="text-muted-foreground"> Business closed this day</span>
            </div>
          </div>
        </div>
      </div>
    ),
    disableBeacon: true,
  },

  // Step 4: Creating Shifts - Overview
  {
    target: '[data-tutorial="calendar-container"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Creating Shifts - Three Approaches</h3>
        </div>
        <p className="text-sm mb-2">
          There are three main approaches to creating shifts:
        </p>

        <div className="space-y-2 text-sm">
          <div>
            <p className="font-medium">1. Use Templates (Recommended)</p>
            <p className="text-muted-foreground text-xs">
              For repeating schedules - create once, apply across multiple days
            </p>
          </div>

          <div>
            <p className="font-medium">2. Create Manually, Save as Template</p>
            <p className="text-muted-foreground text-xs">
              Build a day's schedule, then save it for future use
            </p>
          </div>

          <div>
            <p className="font-medium">3. Copy from Existing Shifts</p>
            <p className="text-muted-foreground text-xs">
              Replicate shifts from one day/week to another
            </p>
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          Let's explore each method in detail...
        </p>
      </div>
    ),
    disableBeacon: true,
  },

  // Step 5: Creating Shifts - Click & Drag
  {
    target: '[data-tutorial="time-grid"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Method 1: Click & Drag</h3>
        </div>
        <p className="text-sm">
          Click and drag on any time cell to create a new shift. The time range
          adjusts automatically as you drag down the calendar.
        </p>
        <p className="text-sm text-muted-foreground">
          This is perfect for creating one-off shifts or when you're first setting up your schedule.
        </p>
      </div>
    ),
    disableBeacon: true,
    spotlightClicks: true,
  },

  // Step 6: Templates System - Shift Templates
  {
    target: '[data-tutorial="shift-templates"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Copy className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Method 2: Shift Templates</h3>
        </div>
        <p className="text-sm">
          Shift templates save individual shift configurations (time, role, break duration)
          for quick reuse.
        </p>
        <p className="text-sm text-muted-foreground">
          Perfect for common shifts like "Morning Manager" or "Evening Cashier".
          Drag a template onto the calendar to create a shift instantly.
        </p>
      </div>
    ),
    disableBeacon: true,
  },

  // Step 7: Day Templates
  {
    target: '[data-tutorial="day-templates"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Copy className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Day Templates</h3>
        </div>
        <p className="text-sm">
          Day templates save complete daily schedules with multiple shifts.
          Apply an entire day's worth of shifts with one drag-and-drop action.
        </p>
        <p className="text-sm text-muted-foreground">
          Ideal for repeating patterns like "Monday Schedule" or "Weekend Shift Plan".
        </p>
      </div>
    ),
    disableBeacon: true,
  },

  // Step 8: Saving Templates
  {
    target: '[data-tutorial="actions-panel"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Copy className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Creating Your Own Templates</h3>
        </div>
        <p className="text-sm">
          To create templates, use the "Save as Template" option when editing shifts,
          or access the template creation panel from the actions sidebar.
        </p>
        <p className="text-sm text-muted-foreground">
          Templates are a huge time-saver for repeating schedules!
        </p>
      </div>
    ),
    disableBeacon: true,
  },

  // Step 9: Copy & Duplicate
  {
    target: '[data-tutorial="calendar-container"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Copy className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Method 3: Copying & Duplicating</h3>
        </div>
        <p className="text-sm mb-2">
          Quickly replicate shifts across days or weeks:
        </p>

        <div className="space-y-2 text-sm">
          <div>
            <span className="font-medium">Advanced Copy Dialog:</span>
            <span className="text-muted-foreground text-xs"> Copy multiple shifts from one date range to another</span>
          </div>

          <div>
            <span className="font-medium">Quick Copy Button:</span>
            <span className="text-muted-foreground text-xs"> Single-day shift duplication</span>
          </div>

          <div>
            <span className="font-medium">Bulk Operations:</span>
            <span className="text-muted-foreground text-xs"> Access from actions panel in edit mode</span>
          </div>
        </div>
      </div>
    ),
    disableBeacon: true,
  },

  // Step 10: Editing Shifts - skip if no shifts exist
  {
    target: '[data-tutorial="shift-card"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Editing Shifts</h3>
        </div>
        <p className="text-sm">
          Click any shift to open the edit panel and modify details like time,
          employee assignment, or notes.
        </p>
        <p className="text-sm text-muted-foreground">
          You can also resize shifts by dragging the edges, or move them by dragging
          the handle at the top.
        </p>
        <p className="text-xs text-muted-foreground mt-2 italic">
          Note: This step requires existing shifts. If you don't have any shifts yet, this step will be skipped.
        </p>
      </div>
    ),
    disableBeacon: true,
    spotlightClicks: true,
  },

  // Step 11: Shift Card - Drag Handle - skip if no shifts exist
  {
    target: '[data-tutorial="shift-drag-handle"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Moving Shifts</h3>
        </div>
        <p className="text-sm">
          Use this drag handle to move shifts to different days or times.
          The shift maintains its duration when moved.
        </p>
        <p className="text-xs text-muted-foreground mt-2 italic">
          Note: This step requires existing shifts. If you don't have any shifts yet, this step will be skipped.
        </p>
      </div>
    ),
    disableBeacon: true,
  },

  // Step 12: Employee Assignment - skip if no shifts exist
  {
    target: '[data-tutorial="shift-employee-select"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Users className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Employee Assignment</h3>
        </div>
        <p className="text-sm">
          Assign employees directly from the shift card using this dropdown.
          You can also leave shifts unassigned and fill them in later.
        </p>
        <p className="text-sm text-muted-foreground">
          Unassigned shifts appear with an orange indicator to help you track
          what still needs attention.
        </p>
        <p className="text-xs text-muted-foreground mt-2 italic">
          Note: This step requires existing shifts. If you don't have any shifts yet, this step will be skipped.
        </p>
      </div>
    ),
    disableBeacon: true,
  },

  // Step 13: Employee List in Actions Panel
  {
    target: '[data-tutorial="employee-list"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Users className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Employee List</h3>
        </div>
        <p className="text-sm">
          Drag employees from this list directly onto shifts to assign them.
          This is a fast way to fill multiple shifts.
        </p>
        <p className="text-sm text-muted-foreground">
          You can also click on employees here to highlight all their shifts on the calendar.
        </p>
      </div>
    ),
    disableBeacon: true,
  },

  // Step 14: Filtering - Overview
  {
    target: '[data-tutorial="filter-dropdown"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Filter className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Filtering & Highlighting</h3>
        </div>
        <p className="text-sm">
          Use this filter to focus on specific employees or shift statuses.
          Filtered shifts are highlighted while others are dimmed.
        </p>
        <p className="text-sm text-muted-foreground">
          This is perfect for checking individual schedules or finding shifts that need attention.
        </p>
      </div>
    ),
    disableBeacon: true,
    spotlightClicks: true,
  },

  // Step 15: Employee Filter
  {
    target: '[data-tutorial="filter-dropdown"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Filter className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Employee Filter</h3>
        </div>
        <p className="text-sm">
          Select one or multiple employees to highlight only their shifts.
          Perfect for checking individual schedules or workload distribution.
        </p>
        <p className="text-sm text-muted-foreground">
          You can see at a glance how many hours each employee is scheduled.
        </p>
      </div>
    ),
    disableBeacon: true,
  },

  // Step 16: Status Filter
  {
    target: '[data-tutorial="filter-dropdown"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Filter className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Status Filter</h3>
        </div>
        <p className="text-sm">
          Filter by shift status (unassigned, in-progress, completed, confirmed, etc.)
          to find shifts that need attention.
        </p>
        <p className="text-sm text-muted-foreground">
          Combine with employee filters to get very specific views of your schedule.
        </p>
      </div>
    ),
    disableBeacon: true,
  },

  // Step 17: Multi-Select & Clear
  {
    target: '[data-tutorial="filter-dropdown"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Filter className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Multi-Select & Clear Filters</h3>
        </div>
        <p className="text-sm">
          You can combine multiple filters to narrow your view. For example,
          show only unassigned shifts for a specific employee.
        </p>
        <p className="text-sm text-muted-foreground">
          Click the X button on the filter to quickly clear all active filters
          and see your full schedule.
        </p>
      </div>
    ),
    disableBeacon: true,
  },

  // Step 18: Actions Panel Toggle
  {
    target: '[data-tutorial="actions-toggle"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Columns className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Actions Panel</h3>
        </div>
        <p className="text-sm">
          Toggle this panel to access editing mode with bulk operations,
          templates, and advanced features.
        </p>
        <p className="text-sm text-muted-foreground">
          The actions panel is your command center for efficient scheduling.
        </p>
      </div>
    ),
    disableBeacon: true,
    spotlightClicks: true,
  },

  // Step 19: Calendar Settings
  {
    target: '[data-tutorial="settings-button"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Settings className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Calendar Settings</h3>
        </div>
        <p className="text-sm mb-2">
          Customize your calendar view with these options:
        </p>

        <div className="space-y-1 text-sm">
          <div>
            <span className="font-medium">Hide Past Days:</span>
            <span className="text-muted-foreground text-xs"> Focus only on upcoming schedule</span>
          </div>

          <div>
            <span className="font-medium">24-Hour Format:</span>
            <span className="text-muted-foreground text-xs"> Switch between 12h/24h time display</span>
          </div>

          <div>
            <span className="font-medium">Display Hours:</span>
            <span className="text-muted-foreground text-xs"> Show total hours per employee/day</span>
          </div>
        </div>
      </div>
    ),
    disableBeacon: true,
    spotlightClicks: true,
  },

  // Step 20: Month Navigation
  {
    target: '[data-tutorial="month-nav"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Month Navigation</h3>
        </div>
        <p className="text-sm">
          Use these controls to navigate between months and jump to specific dates.
          The calendar shows one week at a time by default.
        </p>
        <p className="text-sm text-muted-foreground">
          Click the month name to open a date picker for quick jumps to any date.
        </p>
      </div>
    ),
    disableBeacon: true,
  },

  // Step 21: Keyboard Shortcuts
  {
    target: '[data-tutorial="calendar-container"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Keyboard className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Keyboard Shortcuts</h3>
        </div>
        <p className="text-sm mb-2">
          Speed up your workflow with these shortcuts:
        </p>

        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Cancel template creation</span>
            <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Esc</kbd>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Navigate between shifts</span>
            <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Tab</kbd>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Open shift options</span>
            <div className="flex gap-1">
              <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Enter</kbd>
              <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Space</kbd>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Move between elements</span>
            <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Arrow Keys</kbd>
          </div>
        </div>

        <p className="text-xs text-muted-foreground mt-2">
          More shortcuts available when editing shifts or creating templates
        </p>
      </div>
    ),
    disableBeacon: true,
  },

  // Step 22: Pro Tips
  {
    target: '[data-tutorial="calendar-container"]',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="h-5 w-5 text-green-500" />
          <h3 className="text-base font-semibold">Pro Tips</h3>
        </div>

        <ul className="space-y-1.5 text-sm text-muted-foreground list-disc list-inside">
          <li>Check Schedule Overview Card daily to catch issues early</li>
          <li>Create templates for common shifts to save time</li>
          <li>Use filters to quickly find unassigned shifts</li>
          <li>Toggle the actions panel for powerful bulk operations</li>
          <li>Copy shifts from previous weeks for repeating patterns</li>
        </ul>
      </div>
    ),
    disableBeacon: true,
  },

  // Step 23: Tutorial Complete
  {
    target: 'body',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="h-6 w-6 text-green-500" />
          <h3 className="text-lg font-semibold">Tutorial Complete!</h3>
        </div>
        <p className="text-sm">
          You now know all the features of the calendar system. Start creating
          your schedule and remember you can always:
        </p>

        <ul className="space-y-1 text-sm text-muted-foreground list-disc list-inside">
          <li>Restart this tutorial anytime</li>
          <li>Access the full reference documentation via the help icon</li>
          <li>Experiment with different features - they're all designed to save you time!</li>
        </ul>

        <p className="text-sm font-medium mt-4">
          Happy scheduling! 🎉
        </p>
      </div>
    ),
    disableBeacon: true,
  },
];
